import Pusher, { Channel } from 'pusher-js';
import { Subject } from 'rxjs';
import { ChatMessage } from '../../shared/models/chat';
import EnvironmentUtils from '../../shared/utils/environment-utils';
import EnrouteApiClient from '../../shared/services/enroute-api-client';
import { mapAxiosResponse } from '../../shared/utils/api-utils';

interface MessageEvent {
  chat_channel: string;
  message: ChatMessage;
}

interface SubscriptionErrorEvent {
  chat_channel: string;
  error: any;
}

const getPusherChannelName = (chatChannel: string): string => (
  chatChannel.startsWith('private-') ? chatChannel : `private-${chatChannel}`
);

class PusherChatTransport {
  private pusherClient: Pusher | null = null;

  // Resolves once connected, and is kept (not cleared) after success so a
  // later connect() call short-circuits without reconnecting. Cleared on
  // disconnect/error so the next connect() call builds a fresh client instead
  // of resolving immediately against a dead or disconnected one.
  private connectPromise: Promise<void> | null = null;

  private channels: Map<string, { channel: Channel, pusherChannelName: string }> = new Map();

  private messageSubject = new Subject<MessageEvent>();

  private subscriptionErrorSubject = new Subject<SubscriptionErrorEvent>();

  async connect(orgId: number): Promise<void> {
    if (this.connectPromise) {
      return this.connectPromise;
    }

    const pusherKey = EnvironmentUtils.getVar('PUSHER_KEY');
    const pusherCluster = EnvironmentUtils.getVar('PUSHER_CLUSTER');
    const apiBaseUrl = EnvironmentUtils.getVar('API_ENROUTE');

    this.pusherClient = new Pusher(pusherKey, {
      cluster: pusherCluster,
      channelAuthorization: {
        endpoint: `${apiBaseUrl}/pusher/channel_auth`,
        transport: 'ajax',
        // pusher-js's built-in 'ajax' transport POSTs as form-urlencoded, but
        // this endpoint expects a JSON body, so authorize via our own client.
        customHandler: ({ socketId, channelName }, callback) => {
          EnrouteApiClient.post('/pusher/channel_auth', {
            socket_id: socketId,
            channel_name: channelName,
            org_id: orgId,
          })
            .then((resp) => callback(null, mapAxiosResponse(resp)))
            .catch((error) => callback(error, null));
        },
      },
    });

    this.connectPromise = new Promise<void>((resolve, reject) => {
      this.pusherClient!.connection.bind('connected', () => {
        resolve();
      });

      this.pusherClient!.connection.bind('error', (error: any) => {
        reject(error);
      });
    }).catch((error) => {
      // Failed connect: tear down so the next connect() builds a fresh client
      // instead of short-circuiting against this dead one.
      this.disconnect();
      throw error;
    });

    return this.connectPromise;
  }

  disconnect(): void {
    this.connectPromise = null;
    if (this.pusherClient) {
      this.channels.forEach(({ channel }) => {
        channel.unbind_all();
      });
      this.channels.clear();
      this.pusherClient.disconnect();
      this.pusherClient = null;
    }
  }

  subscribe(chatChannel: string): void {
    if (!this.pusherClient) {
      console.error('Pusher client not connected');
      return;
    }

    if (this.channels.has(chatChannel)) {
      return;
    }

    const pusherChannelName = getPusherChannelName(chatChannel);
    const channel = this.pusherClient.subscribe(pusherChannelName);

    channel.bind('NEW_MESSAGE', (message: ChatMessage) => {
      this.messageSubject.next({
        chat_channel: chatChannel,
        message,
      });
    });

    channel.bind('pusher:subscription_error', (error: any) => {
      console.error(`Pusher subscription failed for channel ${pusherChannelName}:`, error);
      this.subscriptionErrorSubject.next({ chat_channel: chatChannel, error });
    });

    this.channels.set(chatChannel, { channel, pusherChannelName });
  }

  unsubscribe(chatChannel: string): void {
    const entry = this.channels.get(chatChannel);
    if (entry) {
      entry.channel.unbind_all();
      this.pusherClient?.unsubscribe(entry.pusherChannelName);
      this.channels.delete(chatChannel);
    }
  }

  onMessage() {
    return this.messageSubject.asObservable();
  }

  onSubscriptionError() {
    return this.subscriptionErrorSubject.asObservable();
  }
}

export const pusherChatTransport = new PusherChatTransport();

export default pusherChatTransport;
